/* eslint-disable no-continue, no-restricted-syntax */

import { Editor, Node, Range, Text } from 'slate';

// shallow-equal util
const isEqual = (a: any[], b: any[]) =>
  a.length === b.length && a.every((value, i) => b[i] === value);

/**
  Get the boundaries of the selection
  This is useful especially when the selection involves multiple RTE nodes (eg: Typography, ListItems)
 */
export const getBoundaries = (editor: Editor) => {
  const { selection } = editor;

  if (!selection) {
    return [];
  }

  const { anchor, focus } = selection;
  const { path: anchorPath, offset: anchorOffset } = anchor;
  const { path: focusPath, offset: focusOffset } = focus;

  const elements = Node.elements(editor, {
    from: anchorPath,
    to: focusPath,
  });

  const boundaries: Range[] = [];

  for (const element of elements) {
    const [node, path] = element;

    const { children } = node;

    if (!children) {
      continue;
    }

    const hasText = children.filter((child) => Text.isText(child)).length > 0;

    if (!hasText) {
      continue;
    }

    const anchorAncestors = anchorPath.slice(0, anchorPath.length - 1);
    const focusAncestors = focusPath.slice(0, focusPath.length - 1);
    const lastChildPath = children.length - 1;
    const lastChild = children[lastChildPath];

    if (boundaries.length === 0) {
      if (isEqual(anchorAncestors, path)) {
        // Check if the focus comes right after the anchor
        let isFocusSibling = false;

        if (focusAncestors.length === path.length) {
          // If the parents are the same, check if the focus is the immediate sibling
          isFocusSibling = focusAncestors[focusAncestors.length - 1] === path[path.length - 1] + 1;
        } else if (focusAncestors.length === path.length + 1) {
          // This happens when there is a Typography Node, followed by a List and ListItem
          // When the user selects the entire Typography Node, the focus is placed in the ListItem at offset 0
          isFocusSibling = focusAncestors[path.length - 1] === path[path.length - 1] + 1;
        } else if (path.length === focusAncestors.length + 1) {
          // This happens when there is a ListItem, followed by a Typography node outside the parent List
          // When the user selects the entire ListItem, the focus will be placed in the Typography Node at offset 0
          isFocusSibling =
            focusAncestors[focusAncestors.length - 1] === path[focusAncestors.length - 1] + 1;
        }

        // Focus may be invalid
        // This is the case when the user double clicks to select the entire ListItem/Typography element
        const isFocusInvalid = isFocusSibling && focusOffset === 0;

        // If the anchor/focus are in the same element
        if (isEqual(focusAncestors, path) || isFocusInvalid) {
          boundaries.push({
            anchor: {
              path: anchorPath,
              offset: anchorOffset,
            },
            focus: {
              path: isFocusInvalid ? [...path, lastChildPath] : focusPath,
              offset:
                isFocusInvalid && Text.isText(lastChild) ? lastChild.text.length : focusOffset,
            },
          });
          break;
        }

        // Otherwise, select the entire anchor element
        boundaries.push({
          anchor: {
            path: anchorPath,
            offset: anchorOffset,
          },
          focus: {
            path: [...path, lastChildPath],
            offset: Text.isText(lastChild) ? lastChild.text.length : 0,
          },
        });

        continue;
      }

      break;
    }

    /**
     * If the focus is in the element:
     * wrap the start until the specified offset
     */
    if (isEqual(focusAncestors, path)) {
      boundaries.push({
        anchor: {
          path: [...path, 0],
          offset: 0,
        },
        focus: {
          path: focusPath,
          offset: focusOffset,
        },
      });

      break;
    }

    /**
     * For elements that are in-between the anchor and focus element:
     * wrap the entire element
     */
    boundaries.push({
      anchor: {
        path: [...path, 0],
        offset: 0,
      },
      focus: {
        path: [...path, lastChildPath],
        offset: Text.isText(lastChild) ? lastChild.text.length : 0,
      },
    });
  }

  return boundaries;
};
